import { useState, useEffect } from "react";

const PRIMARY_LOGO = "/pwc-logo-primary.png";

export default function IntroAnimation({ onComplete }) {
  const [phase, setPhase] = useState(0);
  // 0: TV turning on
  // 1: Channel tuning / static
  // 2: Broadcast signal found — arena lights
  // 3: Camera flash + logo slam
  // 4: Lower-third graphics + tagline
  // 5: "Live from the territory" + fade out

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 1000),
      setTimeout(() => setPhase(3), 2000),
      setTimeout(() => setPhase(4), 3200),
      setTimeout(() => setPhase(5), 4800),
      setTimeout(() => onComplete(), 5800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      data-testid="intro-animation"
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden cursor-pointer"
      style={{
        background: '#000',
        opacity: phase === 5 ? 0 : 1,
        transition: phase === 5 ? 'opacity 0.8s ease-out' : 'none',
      }}
      onClick={() => onComplete()}
    >
      {/* === TV TURNING ON — white line expands === */}
      {phase === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div style={{
            width: '100%',
            height: '2px',
            background: 'white',
            animation: 'tvTurnOn 0.3s ease-out forwards',
          }} />
        </div>
      )}

      {/* === CHANNEL TUNING / STATIC === */}
      {phase === 1 && (
        <div className="absolute inset-0 z-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          opacity: 0.4,
          animation: 'tvStatic 0.08s infinite',
        }}>
          {/* Channel number */}
          <div className="absolute top-8 left-8">
            <p style={{ fontFamily: 'monospace', fontSize: '24px', color: '#0f0', textShadow: '0 0 10px #0f0' }}>
              CH 45
            </p>
          </div>
        </div>
      )}

      {/* === BROADCAST SIGNAL — ARENA === */}
      {phase >= 2 && (
        <div className="absolute inset-0 z-5" style={{
          background: 'radial-gradient(ellipse at 50% 60%, rgba(30,20,5,1) 0%, #000 80%)',
          opacity: phase >= 2 ? 1 : 0,
          transition: 'opacity 0.5s',
        }}>
          {/* Arena warm glow from above — like house lights */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(176,144,48,0.1) 0%, transparent 50%)',
          }} />
          {/* Arena glow from below — crowd */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 50% 100%, rgba(139,48,32,0.06) 0%, transparent 40%)',
          }} />
        </div>
      )}

      {/* === THREE RING ROPES === */}
      {phase >= 2 && (
        <div className="absolute inset-0 z-20 pointer-events-none">
          {/* Top rope */}
          <div style={{
            position: 'absolute',
            top: '28%',
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent 2%, rgba(200,170,60,0.7) 15%, rgba(200,170,60,0.9) 50%, rgba(200,170,60,0.7) 85%, transparent 98%)',
            opacity: phase >= 3 ? 0.8 : 0,
            transition: 'opacity 0.4s ease-out',
            boxShadow: '0 0 8px rgba(200,170,60,0.3)',
          }} />
          {/* Middle rope */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent 2%, rgba(200,170,60,0.5) 15%, rgba(200,170,60,0.7) 50%, rgba(200,170,60,0.5) 85%, transparent 98%)',
            opacity: phase >= 3 ? 0.6 : 0,
            transition: 'opacity 0.4s ease-out 0.1s',
            boxShadow: '0 0 6px rgba(200,170,60,0.2)',
          }} />
          {/* Bottom rope */}
          <div style={{
            position: 'absolute',
            top: '72%',
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent 2%, rgba(200,170,60,0.7) 15%, rgba(200,170,60,0.9) 50%, rgba(200,170,60,0.7) 85%, transparent 98%)',
            opacity: phase >= 3 ? 0.8 : 0,
            transition: 'opacity 0.4s ease-out 0.2s',
            boxShadow: '0 0 8px rgba(200,170,60,0.3)',
          }} />
        </div>
      )}

      {/* === CAMERA FLASH on logo entrance === */}
      {phase === 3 && (
        <div className="absolute inset-0 z-30" style={{
          background: 'white',
          animation: 'cameraFlash 0.3s ease-out forwards',
        }} />
      )}

      {/* === LOGO SLAM === */}
      <div className="relative z-40 flex flex-col items-center" style={{
        opacity: phase >= 3 ? 1 : 0,
        transform: phase >= 3 ? 'scale(1) translateY(0)' : 'scale(2) translateY(-20px)',
        transition: phase >= 3 ? 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
      }}>
        <img
          src={PRIMARY_LOGO}
          alt="Piedmont Wrestling Club"
          className="w-56 sm:w-72 md:w-96 h-auto"
          style={{
            filter: `drop-shadow(0 0 ${phase >= 3 ? '50px' : '0'} rgba(176,144,48,0.5)) drop-shadow(0 0 100px rgba(176,144,48,0.2))`,
            transition: 'filter 1s ease-out',
          }}
        />
      </div>

      {/* === LOWER THIRD GRAPHIC — like a TV broadcast === */}
      {phase >= 4 && (
        <div className="absolute bottom-[18%] left-0 right-0 z-50 flex justify-center">
          <div style={{
            opacity: phase >= 4 ? 1 : 0,
            transform: phase >= 4 ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            {/* Lower third bar */}
            <div className="relative">
              <div className="flex items-center">
                <div style={{ width: '4px', height: '50px', background: '#B09030' }} />
                <div style={{ background: 'linear-gradient(90deg, rgba(13,13,10,0.95), rgba(13,13,10,0.8))' }} className="px-8 py-3">
                  <p className="font-heading text-lg sm:text-xl text-[#B09030] uppercase tracking-[0.3em]">
                    Return to Tradition
                  </p>
                  <p className="font-body text-[10px] text-[#E8DFC8]/50 uppercase tracking-[0.4em] mt-1">
                    Piedmont Wrestling Club &bull; Live from the Territory
                  </p>
                </div>
                <div style={{ width: '4px', height: '50px', background: '#8B3020' }} />
              </div>
              {/* Top/bottom lines on lower third */}
              <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, #B09030, #D4B84A, #B09030)' }} />
              <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, #B09030, #D4B84A, #B09030)' }} />
            </div>
          </div>
        </div>
      )}

      {/* === ON AIR + LIVE badge === */}
      {phase >= 3 && (
        <div className="absolute top-6 right-6 z-50 flex items-center gap-3" style={{
          opacity: phase >= 3 ? 1 : 0,
          transition: 'opacity 0.4s',
        }}>
          <div className="on-air">Live</div>
        </div>
      )}

      {/* === BROADCAST TOP BAR === */}
      <div className="absolute top-0 left-0 right-0 h-[3px] z-50" style={{
        background: 'linear-gradient(90deg, transparent, #B09030 20%, #D4B84A 50%, #B09030 80%, transparent)',
        opacity: phase >= 3 ? 1 : 0,
        transition: 'opacity 0.3s',
      }} />
      <div className="absolute bottom-0 left-0 right-0 h-[3px] z-50" style={{
        background: 'linear-gradient(90deg, transparent, #B09030 20%, #D4B84A 50%, #B09030 80%, transparent)',
        opacity: phase >= 3 ? 1 : 0,
        transition: 'opacity 0.3s',
      }} />

      {/* === CRT TV OVERLAY === */}
      <div className="absolute inset-0 z-[100] pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px)',
      }} />

      {/* === SCREEN CURVATURE VIGNETTE === */}
      <div className="absolute inset-0 z-[99] pointer-events-none" style={{
        boxShadow: 'inset 0 0 150px rgba(0,0,0,0.5)',
        borderRadius: '8px',
      }} />

      {/* Skip hint */}
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 font-body text-[9px] text-[#E8DFC8]/15 uppercase tracking-[0.3em]"
        style={{ opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.5s' }}
      >
        Tap to skip
      </p>

      <style>{`
        @keyframes tvTurnOn {
          0% { height: 2px; width: 0%; opacity: 1; }
          50% { height: 2px; width: 100%; opacity: 1; }
          100% { height: 100vh; width: 100%; opacity: 0; }
        }
        @keyframes tvStatic {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-1px, 1px); }
          50% { transform: translate(1px, -1px); }
          75% { transform: translate(-1px, -1px); }
          100% { transform: translate(1px, 1px); }
        }
        @keyframes cameraFlash {
          0% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
